import useDataLayer from '@/hooks/useDataLayer/useDataLayer';
import useEpilot from '@/hooks/useEpilot/useEpilot';
import { useIsPromoActive } from '@/hooks/useIsPromoActive/useIsPromoActive';
import usePathname from '@/hooks/usePathname/usePathname';
import useResizeObserver from '@/hooks/useResizeObserver/useResizeObserver';
import { useSafeLayoutEffect } from '@/hooks/useSafeLayoutEffect/useSafeLayoutEffect';
import BackToTopButton from '@uikit/components/BackToTopButton/BackToTopButton';
import EnercityFooter from '@uikit/components/EnercityFooter/EnercityFooter';
import Icon from '@uikit/components/Icon/Icon';
import NewHeader from '@uikit/components/NewHeader/NewHeader';
import ShowroomNav from '@uikit/components/ShowroomNav/ShowroomNav';
import Spacer from '@uikit/components/Spacer/Spacer';
import stripHTMLTags from '@uikit/helpers/stripHTMLTags';
import clsx from 'clsx';
import NextImage from 'next/image';
import Link from 'next/link';
import { useCallback, useRef, useState } from 'react';
import styles from './EMobilityProductPage.module.scss';
import andChargeIllustration from './assets/andcharge-illustration.png';
import businessBackground from './assets/business-background.webp';
import { ReactComponent as EarthIcon } from './assets/earth.svg';
import { ReactComponent as EasyGoIcon } from './assets/easy-go-icon.svg';
import { ReactComponent as EmobilityIcon } from './assets/emobility.svg';
import { ReactComponent as FullServiceIcon } from './assets/full-service.svg';
import mehrfamilienhausBackground from './assets/mehrfamilienhaus-background.webp';
import { ReactComponent as OekoIcon } from './assets/oeko.svg';
import { ReactComponent as PromoBadge } from './assets/promo-badge.svg';
import registrationBackground from './assets/registration-background.webp';
import { ReactComponent as SearchIcon } from './assets/search.svg';
import { ReactComponent as SunIcon } from './assets/sun.svg';
import wallboxBackground from './assets/wallbox-background.webp';
import EMobilityIcons from './icons';

const EMobilityPage = () => {
  const { pushDataLayerEvent } = useDataLayer();
  const pathname = usePathname();

  const [canScrollLeft, setScrollLeft] = useState(false);
  const [canScrollRight, setScrollRight] = useState(true);
  const scrollContainerRef = useRef<HTMLUListElement | null>(null);
  const scrollChildrenRef = useRef<Element[]>([]);
  const scrollBoundary =
    (scrollChildrenRef.current?.[0] as HTMLElement)?.offsetLeft ?? 0;

  const { showJourney } = useEpilot();

  const checkScrollPosition = useCallback(() => {
    if (!scrollContainerRef.current || !scrollChildrenRef.current.length) {
      return;
    }

    const containerWidth =
      scrollContainerRef.current.scrollWidth -
      scrollContainerRef.current.clientWidth;

    setScrollLeft(scrollContainerRef.current.scrollLeft >= 1);
    setScrollRight(containerWidth - scrollContainerRef.current.scrollLeft > 1);

    scrollContainerRef.current
      .querySelector('[aria-current="true"]')
      ?.setAttribute('aria-current', 'false');

    const { current } = scrollChildrenRef.current
      .map((child) => {
        const { left } = child.getBoundingClientRect();
        return { distance: Math.abs(left - scrollBoundary), current: child };
      })
      .toSorted((a, b) => a.distance - b.distance)[0];

    current.setAttribute('aria-current', 'true');
  }, [scrollBoundary, scrollChildrenRef]);

  const handlePaddleClick = (direction: 'left' | 'right') => {
    if (!scrollContainerRef.current) {
      return;
    }

    const currentIndex = scrollChildrenRef.current.findIndex(
      (item) => item.getAttribute('aria-current') === 'true'
    );
    const next =
      scrollChildrenRef.current[
        direction === 'left' ? currentIndex - 1 : currentIndex + 1
      ];

    if (!next) {
      scrollContainerRef.current.scrollTo({
        behavior: 'smooth',
        left:
          scrollChildrenRef.current[currentIndex].getBoundingClientRect().left -
          scrollBoundary +
          scrollContainerRef.current.scrollLeft,
      });

      return;
    }

    scrollChildrenRef.current[currentIndex].setAttribute(
      'aria-current',
      'false'
    );
    next.setAttribute('aria-current', 'true');

    scrollContainerRef.current.scrollTo({
      behavior: 'smooth',
      left:
        next.getBoundingClientRect().left -
        scrollBoundary +
        scrollContainerRef.current.scrollLeft,
    });
  };

  const pushCustomClickEvent =
    (clickContext: string) => ($event: React.SyntheticEvent<HTMLElement>) => {
      pushDataLayerEvent({
        event: 'customClick',
        ...(clickContext
          ? { clickContext: stripHTMLTags(clickContext) }
          : undefined),
        customClickText: $event.currentTarget.innerText,
        customPagePath: pathname.split('?')[0],
      });
    };

  const isPromoActive = useIsPromoActive('ostern2025');

  useSafeLayoutEffect(() => {
    if (!scrollContainerRef.current) {
      return;
    }

    const element = scrollContainerRef.current;

    scrollChildrenRef.current = Array.from(element.children);

    element.addEventListener('scroll', checkScrollPosition, { passive: true });

    return () => {
      element.removeEventListener('scroll', checkScrollPosition);
    };
  }, [checkScrollPosition, scrollContainerRef]);

  useResizeObserver(scrollContainerRef, () => checkScrollPosition());

  return (
    <div className={styles.base}>
      <NewHeader hasPageHeader={false} isFullWidth />

      <ShowroomNav>
        <ShowroomNav.Item
          icon={<EMobilityIcons.Wallbox />}
          title="Wallbox"
          to="https://www.enercity.de/privatkunden/produkte/elektromobilitaet/wallbox"
        />
        <ShowroomNav.Item
          icon={<EMobilityIcons.PublicCharging />}
          title="easyGo"
          to="https://www.enercity.de/privatkunden/produkte/elektromobilitaet/easygo"
        />
        <ShowroomNav.Item
          icon={<EMobilityIcons.MultiFamilyHouse />}
          title="Mehrfamilienhaus"
          to="https://www.enercity.de/privatkunden/produkte/elektromobilitaet/mehrfamilenhaus"
        />
        <ShowroomNav.Item
          icon={<EMobilityIcons.AndCharge />}
          title="&Charge App"
          to="https://www.enercity.de/privatkunden/produkte/elektromobilitaet/andcharge"
        />
        {/* TODO: Enercity plans to review re-enabling this in autumn */}
        {/* <ShowroomNav.Item
          icon={<EMobilityIcons.THG />}
          title="THG-Quote"
          to="https://www.enercity.de/privatkunden/produkte/elektromobilitaet/thg-quote"
        /> */}
        <ShowroomNav.Item
          icon={<EMobilityIcons.Business />}
          title="Business"
          to="https://www.enercity.de/geschaeftskunden/produkte/elektromobilitaet"
        />
        <ShowroomNav.Item
          icon={<EMobilityIcons.Guide />}
          title="Ratgeber"
          to="https://www.enercity.de/privatkunden/produkte/elektromobilitaet/ratgeber"
        />
        <ShowroomNav.Item
          icon={<EMobilityIcons.Phone />}
          title="Smartladen"
          to="https://www.enercity.de/privatkunden/produkte/elektromobilitaet/smartladen"
        />
      </ShowroomNav>

      <main className={styles.contentContainer}>
        <section className={styles.stage}>
          <div className={styles.stageContent}>
            <div className={styles.subtitle}>enercity mobility</div>
            <h1 className={styles.title}>
              Innovative Lade&shy;lösungen für Ihr emissions&shy;freies
              Fahrerlebnis
            </h1>
          </div>
          {isPromoActive && <PromoBadge className={styles.imageBadge} />}
        </section>

        <section
          className={clsx(styles.section, styles.chargingSolutionsSection)}
        >
          <div className={styles.container}>
            <h2 className={styles.title}>Flexible Ladelösungen von enercity</h2>
            <div className={styles.text}>
              Unsere zukunftsfähigen Wallboxen bieten Ihnen nicht nur höchsten
              Komfort, sondern auch die Möglichkeit, sie später an eine
              Solaranlage anzuschließen.
            </div>

            <div className={clsx(styles.gradientCards)}>
              <article
                className={clsx(
                  styles.card,
                  styles.gradientCard,
                  styles.violet
                )}
              >
                <div
                  className={styles.backdrop}
                  style={{
                    backgroundImage: `url(${wallboxBackground.src})`,
                  }}
                />
                <div className={styles.content}>
                  <h3 className={styles.title}>Ihre Wallbox für Zuhause</h3>
                  <div className={styles.text}>
                    Ladestationen von enercity überzeugen durch intuitive
                    Bedienbarkeit bei maximaler Zuverlässigkeit.
                  </div>
                  <Link
                    className={styles.button}
                    href="/privatkunden/produkte/elektromobilitaet/wallbox"
                    onClick={pushCustomClickEvent('Wallbox')}
                  >
                    Jetzt konfigurieren
                  </Link>
                </div>
              </article>
              <article
                className={clsx(
                  styles.card,
                  styles.gradientCard,
                  styles.violet
                )}
              >
                <div
                  className={clsx(styles.backdrop)}
                  style={{
                    backgroundImage: `url(${mehrfamilienhausBackground.src})`,
                  }}
                />
                <div className={styles.content}>
                  <h3 className={styles.title}>Laden im Mehrfamilienhaus</h3>
                  <div className={styles.text}>
                    enercity verwirklicht gemeinschaftliche Ladelösungen für
                    Mehrfamilienhäuser.
                  </div>
                  <Link
                    className={styles.button}
                    href="/privatkunden/produkte/elektromobilitaet/mehrfamilenhaus"
                    onClick={pushCustomClickEvent('Laden im Mehrfamilienhaus')}
                  >
                    Mehr erfahren
                  </Link>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className={clsx(styles.section, styles.mobileChargingSection)}>
          <div className={styles.backdrop} />
          <div className={styles.overlay} />

          <div className={styles.container}>
            <h2 className={styles.title}>Unterwegs laden</h2>
            <div className={styles.text}>
              Unsere Ladestationen sind in der ganzen Region Hannover und in
              immer mehr Städten in Deutschland verteilt. Egal wo Sie sind. Die
              Nächste ist nicht weit.
            </div>

            <div className={styles.cards}>
              <Link
                className={clsx(styles.card, styles.publicChargingStationsCard)}
                href="https://karten.enercity.de/oeffentliches-laden"
                onClick={pushCustomClickEvent('Öffentliche Ladestationen')}
              >
                <div className={styles.backdrop} />

                <div className={styles.content}>
                  <h3 className={styles.title}>Öffentliche Ladestationen</h3>
                  <div className={styles.text}>
                    Finden Sie mit unserer Karte einen Ladepunkt ganz in Ihrer
                    Nähe.
                  </div>
                </div>

                <EMobilityIcons.Arrow className={styles.arrow} />
              </Link>

              <Link
                className={clsx(styles.card, styles.easyGoCard)}
                href="/privatkunden/produkte/elektromobilitaet/easygo"
                onClick={pushCustomClickEvent(
                  'Öffentlich Laden mit easyGo App'
                )}
              >
                <div className={styles.backdrop} />

                <div className={styles.content}>
                  <EasyGoIcon className={styles.icon} height={56} width={56} />

                  <div className={styles.subtitle}>
                    Ihr Begleiter im E-Auto Alltag
                  </div>
                  <h3 className={styles.title}>
                    Öffentlich Laden mit easyGo App
                  </h3>

                  <div className={styles.illustration} />
                </div>

                <EMobilityIcons.Arrow className={styles.arrow} />
              </Link>

              <Link
                className={clsx(styles.card, styles.andChargeCard)}
                href="/privatkunden/produkte/elektromobilitaet/andcharge"
                onClick={pushCustomClickEvent('&Charge-Bonusprogramm')}
              >
                <div className={styles.content}>
                  <h3 className={styles.title}>&Charge-Bonusprogramm</h3>
                  <div className={styles.text}>
                    Mit Feedback-Challenges per &Charge-App das Ladeerlebnis
                    verbessern und Freikilometer sammeln.
                  </div>

                  <ul className={styles.features}>
                    <li>An Challenges teilnehmen & Punkte sammeln</li>
                    <li>Freikilometer erhalten</li>
                    <li>Rabatt beim Kauf einer Ladestation</li>
                  </ul>
                </div>
                {
                  // TODO: replace NextImage with new Img or CustomImage component, based on NextImage
                }
                <NextImage
                  src={andChargeIllustration}
                  className={styles.illustration}
                  unoptimized={true}
                  height={392}
                  width={668}
                  alt="&Charge-Bonusprogramm-illustration"
                />

                <EMobilityIcons.Arrow className={styles.arrow} />
              </Link>
            </div>
          </div>
        </section>

        {/* TODO: Enercity plans to review re-enabling this in autumn & remove Spacer */}
        {/* <THGQuotaBlock pushCustomClickEvent={pushCustomClickEvent} /> */}
        <Spacer autoResponsive defaultSize={2} />

        <section className={styles.benefitsSection}>
          <h2 className={styles.title}>
            <span>Ihre Vorteile bei enercity</span>
          </h2>

          <ul
            className={styles.scrollContainer}
            ref={scrollContainerRef}
            tabIndex={0}
          >
            <li className={styles.item}>
              <FullServiceIcon height="64" width="64" />
              <div>
                <h3 className={styles.title}>Alles aus einer Hand</h3>
                <div>
                  Von der Beratung inklusive Fördermöglichkeiten über die
                  Planung, Installation, bis hin zum Service: bei uns bekommen
                  Sie die richtige Ladelösung aus einer Hand.
                </div>
              </div>
            </li>
            <li className={styles.item}>
              <SearchIcon height="64" width="64" />
              <div>
                <h3 className={styles.title}>Individuelle Lösungen</h3>
                <div>
                  Ob Ein- oder Mehrfamilienhaus, Logistikunternehmen,
                  Einzelhandel oder Bäcker - wir bieten eine optimale Lösung
                  individuell für Ihre nachhaltige Mobilität.
                </div>
              </div>
            </li>
            <li className={styles.item}>
              <EmobilityIcon height="64" width="64" />
              <div>
                <h3 className={styles.title}>Unterwegs laden</h3>
                <div>
                  enercity sorgt für eine intelligente, öffentliche
                  Ladeinfrastruktur in Hannover und Region, so dass Sie
                  jederzeit Ihr E-Auto unterwegs laden können.
                </div>
              </div>
            </li>
            <li className={styles.item}>
              <OekoIcon height="64" width="64" />
              <div>
                <h3 className={styles.title}>Immer CO2-frei laden</h3>
                <div>
                  Bei uns fahren Sie mit Ökostrom und schonen die Umwelt.
                </div>
              </div>
            </li>
            <li className={styles.item}>
              <SunIcon height="64" width="64" />
              <div>
                <h3 className={styles.title}>Mit Sonne vom eigenen Dach</h3>
                <div>
                  Wir bieten Ihnen komplette Lösungen mit Photovoltaik-Anlagen
                  für Ihr Dach.
                </div>
              </div>
            </li>
            <li className={styles.item}>
              <EarthIcon height="64" width="64" />
              <div>
                <h3 className={styles.title}>Intelligent laden</h3>
                <div>
                  Mit smarter Technologie können Sie die Leistung aus dem
                  vorhandenen Hausanschluss optimal ausnutzen und steuern.
                </div>
              </div>
            </li>
          </ul>
          <div className={styles.scrollControls}>
            <button
              aria-disabled={!canScrollLeft}
              onClick={() => handlePaddleClick('left')}
              type="button"
              aria-label="Zum vorherigen Element scrollen"
            >
              <Icon size="iconSize32" variant="action/chevron-left" />
            </button>
            <button
              aria-disabled={!canScrollRight}
              onClick={() => handlePaddleClick('right')}
              type="button"
              aria-label="Zum nächsten Element scrollen"
            >
              <Icon size="iconSize32" variant="action/chevron-right" />
            </button>
          </div>

          <div className={styles.container}>
            <div className={clsx(styles.gradientCards)}>
              <article
                className={clsx(
                  styles.card,
                  styles.gradientCard,
                  styles.enercityGradient
                )}
              >
                <div
                  className={styles.backdrop}
                  style={{
                    backgroundImage: `url(${businessBackground.src})`,
                  }}
                />
                <div className={styles.content}>
                  <h3 className={styles.title}>
                    Elektromobilität für Ihr Business
                  </h3>
                  <div className={styles.text}>
                    Für Ihren Fuhrpark, Ihre Mitarbeitenden, Ihre Kunden, Ihre
                    Gäste, Ihre Mieter und für viele mehr.
                  </div>
                  <Link
                    className={styles.button}
                    href="/geschaeftskunden/produkte/elektromobilitaet"
                    onClick={pushCustomClickEvent(
                      'Elektromobilität für Ihr Business'
                    )}
                  >
                    Jetzt beraten lassen
                  </Link>
                </div>
              </article>
              <article
                className={clsx(
                  styles.card,
                  styles.gradientCard,
                  styles.orange
                )}
              >
                <div
                  className={styles.backdrop}
                  style={{
                    backgroundImage: `url(${registrationBackground.src})`,
                  }}
                />
                <div className={styles.content}>
                  <h3 className={styles.title}>Ladeeinrichtung anmelden</h3>
                  <div className={styles.text}>
                    Ihre neue Ladeeinrichtung ist bereits bestellt oder Sie
                    planen eine zu kaufen? Vor Inbetriebnahme muss diese
                    angemeldet oder sogar genehmigt werden.
                  </div>
                  <Link
                    className={styles.button}
                    href="https://www.enercity-netz.de/anschliessen/ladesaeule"
                    onClick={pushCustomClickEvent('Ladeeinrichtung anmelden')}
                    target="_blank"
                  >
                    Zu enercity Netz
                  </Link>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className={styles.contactSection}>
          <div className={styles.container}>
            <h2 className={styles.title}>
              Sieht aus, als sollten wir uns unterhalten!
            </h2>
            <div className={styles.text}>
              Steigen Sie mit uns in die Elektromobilität ein.
            </div>

            <div className={styles.buttons}>
              <button
                className={styles.button}
                onClick={($event) => {
                  pushCustomClickEvent(
                    'Steigen Sie mit uns in die Elektromobilität ein'
                  )($event);
                  showJourney('fe289e30-3daf-11ee-8299-a971753aa401');
                }}
                type="button"
              >
                Formular für Neukunden
              </button>
              <button
                className={clsx(styles.button, styles.isOutlined)}
                onClick={($event) => {
                  pushCustomClickEvent(
                    'Steigen Sie mit uns in die Elektromobilität ein'
                  )($event);
                  showJourney('2d4c67f0-2bae-11ee-bd7a-2361d349416c');
                }}
                type="button"
              >
                Formular für Bestandskunden
              </button>
            </div>

            <div className={styles.subtitle}>Oder rufen Sie uns an</div>
            <Link
              className={styles.phoneNumber}
              href="tel:+495114303113"
              onClick={pushCustomClickEvent(
                'Steigen Sie mit uns in die Elektromobilität ein'
              )}
            >
              0511.430.3113
            </Link>
          </div>
        </section>
      </main>

      <div className={styles.footerContainer}>
        <BackToTopButton footerOffset={0} />
        <EnercityFooter skewOverlap={false} />
      </div>
    </div>
  );
};

export default EMobilityPage;
